import { CircleHelp } from "lucide-react";
import type { CSSProperties, FormHTMLAttributes, FunctionComponent, ReactNode } from "react";
import { useId } from "react";
import { cn } from "./cn";
import { ControlSizeContext, FormItemContext, type ControlSize } from "./control";
import { Tooltip } from "./tooltip";

export interface FormProps extends FormHTMLAttributes<HTMLFormElement> {
  /** Default size for the controls inside (antd `Form size`). */
  size?: ControlSize;
}

/**
 * Layout-only `<form>` — state and validation stay in react-hook-form. Submission is always
 * `preventDefault`ed so pressing Enter never reloads the page; pass `onSubmit={handleSubmit(fn)}`.
 */
export const Form: FunctionComponent<FormProps> = ({ size, onSubmit, className, children, ...props }) => {
  const handleSubmit: FormHTMLAttributes<HTMLFormElement>["onSubmit"] = (event) => {
    event.preventDefault();
    onSubmit?.(event);
  };

  return (
    <ControlSizeContext.Provider value={size}>
      <form noValidate onSubmit={handleSubmit} className={cn("text-sm", className)} {...props}>
        {children}
      </form>
    </ControlSizeContext.Provider>
  );
};

export type ValidateStatus = "error" | "warning" | "success" | "validating" | "";

export interface FormItemProps {
  label?: ReactNode;
  /** Shows the asterisk; validation itself is react-hook-form's job. */
  required?: boolean;
  /** Help icon after the label. */
  tooltip?: ReactNode;
  /** Message under the control — red when `validateStatus="error"`. */
  help?: ReactNode;
  validateStatus?: ValidateStatus;
  /** Secondary hint under the help text. */
  extra?: ReactNode;
  /** Override the generated id the label points at (the control picks it up from context). */
  htmlFor?: string;
  /**
   * The item holds several controls (a `Compact` row, an editor, a checkbox group): render a
   * `role="group"` named by the label instead of a `<label>` pointing at one id. The controls get
   * no id, help or status from the item, so each one needs its own `aria-label`.
   */
  group?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

/** Vertical label + control + help block (antd `Form.Item`, `layout="vertical"`). Default `mb-6`. */
export const FormItem: FunctionComponent<FormItemProps> = ({
  label,
  required,
  tooltip,
  help,
  validateStatus,
  extra,
  htmlFor,
  group,
  className,
  style,
  children,
}) => {
  const generatedId = useId();
  const id = htmlFor ?? generatedId;
  const labelId = `${id}-label`;
  const helpId = `${id}-help`;
  const extraId = `${id}-extra`;
  const describedBy = [help && helpId, extra && extraId].filter(Boolean).join(" ") || undefined;
  const status = validateStatus === "error" || validateStatus === "warning" ? validateStatus : undefined;
  // A shared id would duplicate it in the DOM, and the status / help would flag every control.
  const context = group ? {} : { id, status, describedBy };

  const labelContent = (
    <>
      {required && <span className="text-noa-danger">*</span>}
      {label}
      {tooltip && (
        <Tooltip title={tooltip}>
          <CircleHelp className="size-3.5 cursor-help text-noa-muted" />
        </Tooltip>
      )}
    </>
  );
  const labelClassName = "inline-flex items-center gap-1 pb-2 text-sm text-noa";

  return (
    <FormItemContext.Provider value={context}>
      <div
        className={cn("mb-6 flex flex-col", className)}
        style={style}
        {...(group && {
          role: "group",
          "aria-labelledby": label ? labelId : undefined,
          "aria-describedby": describedBy,
        })}
      >
        {label &&
          (group ? (
            <span id={labelId} className={labelClassName}>
              {labelContent}
            </span>
          ) : (
            <label htmlFor={id} className={labelClassName}>
              {labelContent}
            </label>
          ))}
        {children}
        {help && (
          <div
            id={helpId}
            role={status === "error" ? "alert" : undefined}
            className={cn(
              "pt-0.5 text-sm",
              status === "error" ? "text-noa-danger" : status === "warning" ? "text-noa-warning" : "text-noa-muted"
            )}
          >
            {help}
          </div>
        )}
        {extra && (
          <div id={extraId} className="pt-0.5 text-sm text-noa-muted">
            {extra}
          </div>
        )}
      </div>
    </FormItemContext.Provider>
  );
};

export interface CompactProps {
  className?: string;
  children?: ReactNode;
}

/**
 * Joins adjacent controls/buttons into one bordered group (antd `Space.Compact`). Bare `<input>`
 * children are skipped when picking the first/last child: Base UI `Select` renders a hidden one
 * next to its trigger.
 */
export const Compact: FunctionComponent<CompactProps> = ({ className, children }) => (
  <div
    className={cn(
      "flex items-stretch",
      "[&>*]:relative [&>*]:rounded-none [&>*:focus-within]:z-10 [&>*:hover]:z-10",
      "[&>:nth-child(1_of_:not(input))]:rounded-s-md [&>:nth-last-child(1_of_:not(input))]:rounded-e-md",
      "[&>:not(input):not(:nth-child(1_of_:not(input)))]:-ms-px",
      className
    )}
  >
    {children}
  </div>
);
